import api from "./api";

export interface SupportMessage {
  id: number;
  sender_id: number;
  sender_name: string;
  recipient_id: number;
  recipient_name: string;
  content: string;
  type: "text" | "image";
  file_url?: string;
  file_name?: string;
  status: "sent" | "delivered" | "read";
  timestamp: string;
}

interface ListMessagesResponse {
  messages: SupportMessage[];
  total: number;
}

interface SendMessageRequest {
  content: string;
  type?: "text" | "image";
  file_url?: string;
  file_name?: string;
  recipient_id?: number;
}

interface SendMessageResponse {
  message: SupportMessage;
  success: boolean;
}

export interface SupportRoom {
  user_id: number;
  user_name: string;
  user_email: string;
  last_message: string;
  last_message_time: string;
  unread_count: number;
}

interface ListRoomsResponse {
  rooms: SupportRoom[];
  total: number;
}

/**
 * Lista todas as mensagens de suporte do usuário logado
 * Para o suporte (ID 1), pode filtrar por sala usando room_user_id
 */
export async function listSupportMessages(
  roomUserId?: number,
): Promise<ListMessagesResponse> {
  try {
    const params = roomUserId ? { room_user_id: roomUserId } : {};
    const response = await api.get("/support/messages", { params });
    return response.data;
  } catch (error) {
    console.error("Erro ao listar mensagens de suporte:", error);
    throw error;
  }
}

/**
 * Lista todas as salas (usuários) que conversaram com o suporte
 * Apenas para o usuário de suporte (ID 1)
 */
export async function listSupportRooms(): Promise<ListRoomsResponse> {
  try {
    const response = await api.get("/support/rooms");
    return response.data;
  } catch (error) {
    console.error("Erro ao listar salas de suporte:", error);
    throw error;
  }
}

/**
 * Faz upload de um arquivo (imagem) para mensagem de suporte
 */
export async function uploadSupportFile(
  file: File,
): Promise<{ url: string; fileName: string; originalName: string } | null> {
  try {
    // Converte arquivo para base64
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onload = async () => {
        try {
          const base64String = reader.result as string;

          const response = await api.post("/conversations/upload-file/", {
            base64: base64String,
            file_name: file.name,
          });

          if (response.data && response.data.url) {
            resolve({
              url: response.data.url,
              fileName: response.data.file_name,
              originalName: response.data.original_name || file.name,
            });
          } else {
            reject(new Error("Resposta inválida do servidor"));
          }
        } catch (error) {
          console.error("Erro ao fazer upload do arquivo:", error);
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error("Erro ao ler arquivo"));
      };

      reader.readAsDataURL(file);
    });
  } catch (error) {
    console.error("Erro ao fazer upload do arquivo:", error);
    return null;
  }
}

/**
 * Envia uma nova mensagem de suporte
 */
export async function sendSupportMessage(
  data: SendMessageRequest,
): Promise<SendMessageResponse> {
  try {
    const response = await api.post("/support/messages/send", data);
    return response.data;
  } catch (error) {
    console.error("Erro ao enviar mensagem de suporte:", error);
    throw error;
  }
}

const supportService = {
  listSupportMessages,
  listSupportRooms,
  sendSupportMessage,
  uploadSupportFile,
};

export default supportService;
